import React from 'react';
import { Classable, HasChildren } from '@shapeable/types';
import styled, { css, useTheme } from 'styled-components';
import { breakpoints } from '@shapeable/theme';
import { 
  PageContextDefinition, FormProvider, ModalStateProvider, 
  AppStateProvider, useReady, 
  WARM_GREY, 
  PersonValue,
  LayoutShell,
  SiteFooterLayout,
  UiProvider,
  UiConfig,
  DataHook,
  ComponentSwapProvider,
  OrganisationCard,
  IndicatorLayoutsProvider,
  IndicatorGroupLayoutsProvider,
} from '@shapeable/ui';
import { DEFAULT_MAP_ENTITY_TYPE_NAMES, KELP_HEALTH_COLOUR_MAP, KEY_SERVICES_GRADIENT_STOPS, STRESSORS_COLOUR_MAP } from '../data';
import * as pageLayouts from './page-layouts';
import * as sliceLayouts from './slice-layouts';
import * as blockLayouts from './block-layouts';
import * as icons from './icons';

import { RestorationProjectsProvider } from '../gatsby/providers/restoration-projects-provider';
import { LinkButton } from './elements/link-button';
import { OrganisationsProvider } from '../gatsby/providers/organisations-provider';
import { OrganisationPledgesProvider } from '../gatsby/providers/organisation-pledges-provider';
import { OrganisationTooltip } from './entities/organisation-tooltip';
import { OrganisationPledgeTooltip } from './entities/organisation-pledge-tooltip';
import { DARK_BLUE, LayoutStyles, OCEAN_BLUE, ORANGE, themeOverrides } from '../theme';
import { SiteSidebarProvider } from './context/site-sidebar-provider';
import { FeedEntriesProvider } from '../gatsby/providers/feed-entries-provider';
import { CitationsProvider } from '../gatsby/providers/citations-provider';
import { ArticleTypeIcon } from './icons/article-type-icon';
import { VideosProvider } from '../gatsby/providers/videos-provider';
import { PagesProvider } from '../gatsby/providers/pages-provider';
import { PostsProvider } from '../gatsby/providers/posts-provider';
import { MapConfig, MapState, MapProvider } from '@shapeable/maps';
import { LocationIcon, OrganisationTypeIcon, PersonIcon, PledgeIcon, RestorationProjectIcon } from '@shapeable/icons';
import { EntityMapPanel } from './entities/entity-map-panel';
import { RestorationProjectTooltip } from './entities/restoration-project-tooltip';
import { MapPledgeIcon } from './elements/map-pledge-icon';
import { RestorationProjectMarker } from './entities/restoration-project-marker';
import { useRestorationProjects } from '../hooks/use-restoration-projects';
import { LinearNavigationProvider } from './providers/linear-navigation-provider';
import { MenusNavigationProvider } from './providers/menus-navigation-provider';
import { RestorationProjectCard } from './entities/restoration-project-card';
import { FeedEntryCard } from './entities/feed-entry-card';
import { IndicatorLayoutFeatureCount, IndicatorLayoutGrowthChart, IndicatorLayoutKelpConditionBar, IndicatorLayoutMetricValue, IndicatorLayoutTrailBarChart, IndicatorLayoutValueBubble, IndicatorLayoutValueLabel } from './indicator-layouts';
import { IndicatorGroupLayoutListFallback } from './indicator-group-layouts/indicator-group-layout-list-fallback';
import { CitationCard } from './entities/citation-card';
import { RegionsProvider } from '../gatsby/providers/regions-provider';
import { RegionCard } from './entities/region-card';
import { VideoCard } from './entities/video-card';
import { IndicatorGroupLayoutRegionValueBars } from './indicator-group-layouts/indicator-group-layout-region-value-bars';
import { StressorsLegend } from './elements/stressors-legend';
import { KeyServicesLegend } from './elements/key-services-legend';
const initialMapState: MapState = {
  isVisible: false,
  isInitialized: false,
  zoom: 2,
  entityTypeNames: DEFAULT_MAP_ENTITY_TYPE_NAMES,
  latitude: -5.441022303718128,
  longitude: 117.59765625000352,
};

export type LayoutPropTypes = Classable & HasChildren & {
  path?: string;
  data?: any;
  pageContext?: PageContextDefinition;
};

const LayoutDefaultProps: LayoutPropTypes = {
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    ${LayoutStyles};
  `,
  tablet: css`
  `,
});

const MapStyles = breakpoints({
  base: css`
    
  `,
});

const FooterStyles = breakpoints({
  base: css`
    
  `,
});


// -------- Components -------->

const My = {
  Container: styled(LayoutShell)`${ContainerStyles}`,
  Map: styled(EntityMapPanel)`${MapStyles}`,
  Footer: styled(SiteFooterLayout)`${FooterStyles}`,
};

export const Layout: React.FC<LayoutPropTypes> = (props) => {
  const { className, children } = props;
  const { isReady } = useReady();
  
  const mapConfig: MapConfig = {
    initialMapState,
    showZoomLevel: true,
    entityTypeMapMarkers: {
      RestorationProject: RestorationProjectMarker,
      OrganisationPledge: MapPledgeIcon,
    },
    availableEntityTypeNames: ['RestorationProject', 'ProtectionArea', 'Organisation', 'OrganisationPledge']
  };

  const UiConfig: UiConfig = {
    layouts: { pageLayouts, sliceLayouts, blockLayouts, defaultPageLayout: pageLayouts.PageLayoutDefault },
    theme: themeOverrides,
    icons: icons,
    menuBar: {
      // backgroundColor: DARK_BLUE,
    },
    search: {
      isAvailable: true,
      extraDataHooks: {
        RestorationProject: useRestorationProjects as DataHook,
      },
    },
    header: {
      showLinearNavigation: false,
      showThemeMode: true,
      variant: 'overlay',
      
    },
    footer: {
      buttonColor: OCEAN_BLUE,
      backgroundColor: WARM_GREY,
    },
    entity: {
      labels: {
        Organisation: 'Organisation',
        OrganisationPledge: 'Pledge',
        RestorationProject: 'Project',
        ProtectionArea: 'Protection Area',
      },
      cards: {
        RestorationProject: RestorationProjectCard,
        Organisation: OrganisationCard,
        FeedEntry: FeedEntryCard,
        Citation: CitationCard,
        Region: RegionCard,
        Video: VideoCard,
      },
      dataProviders: [
        CitationsProvider, FeedEntriesProvider, OrganisationsProvider, 
        OrganisationPledgesProvider, PagesProvider, PostsProvider, 
        RestorationProjectsProvider, VideosProvider, RegionsProvider,
      ],
      icons: {
        OrganisationType: OrganisationTypeIcon,
        FeedEntryType: ArticleTypeIcon,
        Author: PersonIcon,
        News: icons.NewsIcon,
        OrganisationPledge: PledgeIcon,
        RestorationProject: LocationIcon,
        ProtectionArea: RestorationProjectIcon,
      },
      tooltips: {
        Organisation: OrganisationTooltip,
        OrganisationPledge: OrganisationPledgeTooltip,
        RestorationProject: RestorationProjectTooltip,
      },
      values: {
        Person: PersonValue,
      },
    },
    componentProps: {
      SegmentBar: {
        maxValue: 4,
      },
      IndicatorGroupLayoutGrid: {
        countIndicators: 'all',
      },
      IndicatorGroupLayoutSegmentChart: {
        colourMap: STRESSORS_COLOUR_MAP,
        maxValue: 5,
        segmentMode: 'single',
        ring: { count: 5 },
        animate: true,
        legend: <StressorsLegend />,
      },
      IndicatorGroupLayoutScaledBubbleRow: {
        colourStops: KEY_SERVICES_GRADIENT_STOPS,
        maxValue: 5,
        valuePrecision: 1,
        legend: <KeyServicesLegend />,
      },
      IndicatorGroupLayoutPspChart: {
        maxValue: 4,
      },
      IndicatorLayoutValueBar: {
        barColours: KELP_HEALTH_COLOUR_MAP,
      },
      SocialLinks: {
        colors: {
          fill: OCEAN_BLUE,
        }
      },
      SlideControls: {
        colors: {
          background: 'light',
          hover: {
            background: ORANGE,
          },
          active: {
            background: OCEAN_BLUE,
          }
        },
        arrowColors:{
          hover:{
            fill: OCEAN_BLUE,
          }
        }
      },
      ContentEntityBlock: {
        colors: {
          text: OCEAN_BLUE,
        }
      },
      SiteFooterLayout: {
        colors: {
          background: WARM_GREY,
        }
      },
      PageHeader: {
        themeMode: 'dark',
      },
      NavigationMenuItem: {
        colors: {
          hover: {
            background: ORANGE,
            text: 'light',
            fill: 'light',
          },
        }
      },
      MenuItem: {
        colors: {
          hover: {
            background: ORANGE,
            fill: 'light',
            text: 'light',
          },
          active: {
            text: ORANGE
          }
        }
      },
      MenuItemLink: {
        colors: {
          hover: {
            text: ORANGE,
            fill: ORANGE,
          },
        }
      },
      SiteMenuBar: {
        colors: {
          background: DARK_BLUE + 'F1',
        },
      },
    },
  }

  return (
    <ComponentSwapProvider value={{
      LinkButton: LinkButton,
    }}>
    <UiProvider value={UiConfig}>
    <MapProvider value={mapConfig}>
    <ModalStateProvider>
    <FormProvider>
    <SiteSidebarProvider>
    <LinearNavigationProvider>
    <IndicatorGroupLayoutsProvider value={{
      IndicatorGroupLayoutListFallback,
      IndicatorGroupLayoutRegionValueBars,
    }}>
    <IndicatorLayoutsProvider value={{
      IndicatorLayoutValueBubble,
      IndicatorLayoutFeatureCount,
      IndicatorLayoutTrailBarChart,
      IndicatorLayoutGrowthChart,
      IndicatorLayoutValueLabel,
      IndicatorLayoutMetricValue,
      IndicatorLayoutKelpConditionBar,
    }}>
    <MenusNavigationProvider>
      <My.Container 
        includeTooltip
        hideOverflow={false}
        tooltipProps={
          {
            backgroundColor: '#2496BD',
            textColor: '#FFF',
            className: 'shp--react-tooltip',
          }
        }
        className={className}>
        {
          isReady && <My.Map />
        }
        {children}
        <My.Footer />
      </My.Container>
    </MenusNavigationProvider>
    </IndicatorLayoutsProvider>
    </IndicatorGroupLayoutsProvider>
    </LinearNavigationProvider>
    </SiteSidebarProvider>
    </FormProvider>
    </ModalStateProvider>
    </MapProvider>
    </UiProvider>
    </ComponentSwapProvider>
  );
  
};

Layout.defaultProps = LayoutDefaultProps;

